import NavBar from '../components/NavBar';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

function formatBytes(b) {
  if (!b || b === 0) return '0 B';
  const k = 1024, sizes = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(Math.max(b,1))/Math.log(k));
  return parseFloat((b/Math.pow(k,i)).toFixed(1)) + ' ' + sizes[i];
}
function formatSecs(s) {
  if (!s) return '0m';
  const h = Math.floor(s/3600), m = Math.floor((s%3600)/60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; }

export default function UsageReports() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/usage?days=${days}`);
      setData(d);
    } catch {}
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const token = localStorage.getItem('ms_token');
    const a = document.createElement('a');
    a.href = `/api/usage/export?days=${days}&token=${token}`;
    a.download = `usage-report-${days}d.csv`;
    a.click();
  };

  const s = {
    page: { minHeight:'100vh', background:'var(--bg)' },
    nav: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 32px', height:'58px', borderBottom:'1px solid var(--border)', background:'var(--surface)', position:'sticky', top:0, zIndex:10 },
    navLogo: { display:'flex', alignItems:'center', gap:'9px', fontWeight:'800', fontSize:'17px' },
    navDot: { width:'9px', height:'9px', borderRadius:'50%', background:'var(--signal)', boxShadow:'0 0 8px var(--signal)' },
    navRight: { display:'flex', alignItems:'center', gap:'10px' },
    navBtn: (h) => ({ fontSize:'13px', padding:'6px 14px', border:`1px solid ${h?'rgba(0,230,118,0.3)':'var(--border)'}`, borderRadius:'7px', background:'transparent', color:h?'var(--signal)':'var(--text-muted)', cursor:'pointer' }),
    main: { maxWidth:'1100px', margin:'0 auto', padding:'36px 24px' },
    header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'28px', flexWrap:'wrap', gap:'12px' },
    title: { fontSize:'24px', fontWeight:'800', letterSpacing:'-0.5px' },
    controls: { display:'flex', gap:'8px', alignItems:'center' },
    periodBtn: (active) => ({ padding:'6px 14px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer', background:active?'var(--signal)':'var(--surface)', color:active?'#000':'var(--text-muted)', border:`1px solid ${active?'var(--signal)':'var(--border)'}` }),
    exportBtn: { padding:'6px 14px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer', background:'var(--surface2)', color:'var(--text)', border:'1px solid var(--border)' },
    statGrid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px', marginBottom:'24px' },
    statCard: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'10px', padding:'16px 18px' },
    statLabel: { fontSize:'11px', fontWeight:'700', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'6px' },
    statValue: { fontSize:'22px', fontWeight:'800', letterSpacing:'-0.5px' },
    statSub: { fontSize:'12px', color:'var(--text-muted)', marginTop:'3px' },
    card: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'20px 24px', marginBottom:'16px' },
    cardTitle: { fontSize:'14px', fontWeight:'700', marginBottom:'16px' },
    table: { width:'100%', borderCollapse:'collapse' },
    th: { textAlign:'left', padding:'9px 14px', fontSize:'11px', fontWeight:'700', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', borderBottom:'1px solid var(--border)', background:'var(--surface2)' },
    td: { padding:'12px 14px', fontSize:'13px', borderBottom:'1px solid var(--border)', verticalAlign:'middle' },
    roleBadge: (r) => {
      const c = {admin:'#00e676',manager:'#f59e0b',dj:'#9146ff'}[r]||'#5c6b82';
      return { fontSize:'10px', fontWeight:'700', padding:'2px 7px', borderRadius:'4px', background:`${c}15`, color:c, border:`1px solid ${c}30` };
    },
    barWrap: { height:'5px', background:'var(--surface2)', borderRadius:'3px', overflow:'hidden', marginTop:'4px', minWidth:'80px' },
    bar: (pct, color) => ({ height:'100%', width:`${Math.min(pct,100)}%`, background:color||'var(--signal)', borderRadius:'3px' }),
    chart: { display:'flex', alignItems:'flex-end', gap:'3px', height:'80px', padding:'0 0 4px' },
    chartBar: (pct) => ({ flex:1, background:`rgba(0,230,118,${0.2+pct*0.8})`, borderRadius:'2px 2px 0 0', minHeight:'2px', height:`${Math.max(pct*100,2)}%`, transition:'height 0.3s' }),
    chartLabel: { display:'flex', gap:'3px', marginTop:'4px' },
    platRow: { display:'flex', alignItems:'center', gap:'12px', padding:'10px 0', borderBottom:'1px solid var(--border)' },
    platName: { width:'120px', fontSize:'13px', fontWeight:'600', textTransform:'capitalize' },
    platBar: { flex:1 },
    platStats: { display:'flex', gap:'16px', fontSize:'12px', color:'var(--text-muted)', width:'180px', justifyContent:'flex-end' },
  };

  const maxStreamSecs = data ? Math.max(...(data.userUsage||[]).map(u=>u.total_stream_secs||0), 1) : 1;
  const maxDailyCount = data ? Math.max(...(data.dailyStreams||[]).map(d=>d.stream_count||0), 1) : 1;
  const maxPlatCount  = data ? Math.max(...(data.platformStats||[]).map(p=>p.stream_count||0), 1) : 1;

  return (
    <NavBar>
      <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.navLogo}><div style={s.navDot}/>MultiStream</div>
        <div style={s.navRight}>
          <button style={s.navBtn(false)} onClick={()=>navigate('/dashboard')}>Dashboard</button>
          <button style={s.navBtn(false)} onClick={()=>navigate('/settings')}>Settings</button>
          {user?.role==='admin'&&<button style={s.navBtn(true)} onClick={()=>navigate('/admin')}>Users</button>}
          <button style={s.navBtn(false)} onClick={logout}>Sign out</button>
        </div>
      </nav>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Usage Reports</div>
            <div style={{fontSize:'14px',color:'var(--text-muted)',marginTop:'4px'}}>Dennery FM 88.9 — Stream analytics</div>
          </div>
          <div style={s.controls}>
            {[7,30,90].map(d=>(
              <button key={d} style={s.periodBtn(days===d)} onClick={()=>setDays(d)}>{d}d</button>
            ))}
            <button style={s.exportBtn} onClick={exportCsv}>↓ CSV</button>
          </div>
        </div>

        {loading ? (
          <div style={{color:'var(--text-muted)',padding:'32px 0'}}>Loading…</div>
        ) : (
          <>
            {/* Totals */}
            <div style={s.statGrid}>
              <div style={s.statCard}>
                <div style={s.statLabel}>Total Streams</div>
                <div style={s.statValue}>{data?.totals?.total_streams||0}</div>
                <div style={s.statSub}>{data?.totals?.active_users||0} active DJs</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Stream Time</div>
                <div style={s.statValue}>{formatSecs(data?.totals?.total_stream_secs)}</div>
                <div style={s.statSub}>last {days} days</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Recordings</div>
                <div style={s.statValue}>{data?.totals?.total_recordings||0}</div>
                <div style={s.statSub}>{formatBytes(data?.totals?.total_recording_bytes)}</div>
              </div>
              <div style={s.statCard}>
                <div style={s.statLabel}>Data In</div>
                <div style={s.statValue}>{formatBytes(data?.totals?.total_bytes_in)}</div>
                <div style={s.statSub}>ingest bandwidth</div>
              </div>
            </div>

            {/* Daily chart */}
            {data?.dailyStreams?.length > 0 && (
              <div style={s.card}>
                <div style={s.cardTitle}>Daily Streams — last {days} days</div>
                <div style={s.chart}>
                  {data.dailyStreams.map((d,i) => (
                    <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'2px'}}>
                      <div style={{fontSize:'10px',color:'var(--text-muted)'}}>{d.stream_count}</div>
                      <div style={{...s.chartBar(d.stream_count/maxDailyCount),width:'100%'}} title={`${d.date}: ${d.stream_count} streams`}/>
                    </div>
                  ))}
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'11px',color:'var(--text-muted)',marginTop:'4px'}}>
                  <span>{data.dailyStreams[0]?.date}</span>
                  <span>{data.dailyStreams[data.dailyStreams.length-1]?.date}</span>
                </div>
              </div>
            )}

            {/* Per-user stats */}
            <div style={s.card}>
              <div style={s.cardTitle}>Per DJ — last {days} days</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>DJ</th>
                    <th style={s.th}>Role</th>
                    <th style={s.th}>Streams</th>
                    <th style={s.th}>Stream Time</th>
                    <th style={s.th}>Recordings</th>
                    <th style={s.th}>Storage Used</th>
                    <th style={s.th}>Last Stream</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.userUsage||[]).map((u,i)=>(
                    <tr key={u.id} style={{borderBottom:'1px solid var(--border)'}}>
                      <td style={s.td}>
                        <div style={{fontWeight:'600'}}>{u.name}</div>
                        <div style={{fontSize:'12px',color:'var(--text-muted)'}}>{u.email}</div>
                      </td>
                      <td style={s.td}><span style={s.roleBadge(u.role)}>{u.role}</span></td>
                      <td style={s.td}>{u.stream_count||0}</td>
                      <td style={s.td}>
                        <div>{formatSecs(u.total_stream_secs)}</div>
                        <div style={s.barWrap}><div style={s.bar((u.total_stream_secs||0)/maxStreamSecs*100)}/></div>
                      </td>
                      <td style={s.td}>{u.recording_count||0}</td>
                      <td style={s.td}>{formatBytes(u.recording_bytes)}</td>
                      <td style={s.td}>{formatDate(u.last_stream_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Platform breakdown */}
            {data?.platformStats?.length > 0 && (
              <div style={s.card}>
                <div style={s.cardTitle}>Platform Performance — last {days} days</div>
                {data.platformStats.map((p,i)=>(
                  <div key={i} style={s.platRow}>
                    <div style={s.platName}>{p.label||p.platform}</div>
                    <div style={s.platBar}>
                      <div style={s.barWrap}>
                        <div style={s.bar(p.stream_count/maxPlatCount*100,'var(--signal)')}/>
                      </div>
                    </div>
                    <div style={s.platStats}>
                      <span style={{color:'var(--signal)'}}>{p.success_count} ok</span>
                      {p.error_count>0&&<span style={{color:'var(--danger)'}}>{p.error_count} err</span>}
                      <span>{p.stream_count} total</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </NavBar>
  );
}
